import {
  SINGLE_ONE,
  SINGLE_FIVE,
  STRAIGHT_FULL,
  STRAIGHT_2_6,
  STRAIGHT_1_5,
  nOfAKind,
} from './rules.js';

type Counts = number[]; // length 7, index 1..6 used

function toCounts(dice: number[]): Counts {
  const c = new Array(7).fill(0) as Counts;
  for (const d of dice) c[d]++;
  return c;
}

function totalLeft(c: Counts): number {
  let n = 0;
  for (let i = 1; i <= 6; i++) n += c[i];
  return n;
}

function bestPartition(c: Counts): { points: number; usedAll: boolean } {
  if (totalLeft(c) === 0) return { points: 0, usedAll: true };

  let best = { points: -1, usedAll: false };

  const tryBranch = (mutate: () => () => void, gain: number): void => {
    const undo = mutate();
    const sub = bestPartition(c);
    undo();
    if (!sub.usedAll) return;
    const total = gain + sub.points;
    if (total > best.points) best = { points: total, usedAll: true };
  };

  for (let face = 1; face <= 6; face++) {
    if (c[face] >= 6) {
      tryBranch(() => {
        c[face] -= 6;
        return () => { c[face] += 6; };
      }, nOfAKind(face, 6));
    }
  }

  if (c[1] >= 1 && c[2] >= 1 && c[3] >= 1 && c[4] >= 1 && c[5] >= 1 && c[6] >= 1) {
    tryBranch(() => {
      for (let i = 1; i <= 6; i++) c[i]--;
      return () => { for (let i = 1; i <= 6; i++) c[i]++; };
    }, STRAIGHT_FULL);
  }

  for (let face = 1; face <= 6; face++) {
    if (c[face] >= 5) {
      tryBranch(() => {
        c[face] -= 5;
        return () => { c[face] += 5; };
      }, nOfAKind(face, 5));
    }
  }
  for (let face = 1; face <= 6; face++) {
    if (c[face] >= 4) {
      tryBranch(() => {
        c[face] -= 4;
        return () => { c[face] += 4; };
      }, nOfAKind(face, 4));
    }
  }

  if (c[2] >= 1 && c[3] >= 1 && c[4] >= 1 && c[5] >= 1 && c[6] >= 1) {
    tryBranch(() => {
      for (let i = 2; i <= 6; i++) c[i]--;
      return () => { for (let i = 2; i <= 6; i++) c[i]++; };
    }, STRAIGHT_2_6);
  }
  if (c[1] >= 1 && c[2] >= 1 && c[3] >= 1 && c[4] >= 1 && c[5] >= 1) {
    tryBranch(() => {
      for (let i = 1; i <= 5; i++) c[i]--;
      return () => { for (let i = 1; i <= 5; i++) c[i]++; };
    }, STRAIGHT_1_5);
  }

  for (let face = 1; face <= 6; face++) {
    if (c[face] >= 3) {
      tryBranch(() => {
        c[face] -= 3;
        return () => { c[face] += 3; };
      }, nOfAKind(face, 3));
    }
  }

  if (c[1] >= 1) {
    tryBranch(() => {
      c[1]--;
      return () => { c[1]++; };
    }, SINGLE_ONE);
  }
  if (c[5] >= 1) {
    tryBranch(() => {
      c[5]--;
      return () => { c[5]++; };
    }, SINGLE_FIVE);
  }

  return best.points >= 0 ? best : { points: 0, usedAll: false };
}

/**
 * Returns the best score achievable by using ALL provided dice as one or more
 * scoring combinations. If no partition uses every die, returns valid: false.
 */
export function scoreSelection(dice: number[]): { points: number; valid: boolean } {
  if (dice.length === 0) return { points: 0, valid: false };
  const counts = toCounts(dice);
  const res = bestPartition(counts);
  return { points: res.points, valid: res.usedAll };
}

/**
 * True if a fresh roll has NO scoring combination at all → bust.
 */
export function isBust(dice: number[]): boolean {
  if (dice.length === 0) return false;
  const c = toCounts(dice);
  if (c[1] >= 1 || c[5] >= 1) return false;
  for (let face = 1; face <= 6; face++) {
    if (c[face] >= 3) return false;
  }
  return true;
}
